/* global G */
import { curry, setKey } from 'lib/util'
import PlatformEvent from 'lib/util/event'
import refresh from 'lib/sequence/model/api/refresh'
import sequenceComponentFind from 'lib/sequence/component/children/find'
import { show } from 'lib/sequence/component/state/hidden'
import setStepTab, { CONFIRM, LIST } from 'app/_shared/events/setStepTab'
import { detail } from 'app/_shared/events'
import { expose } from 'app/_shared/events/device/find'
import undefinedModelLabel from 'app/_shared/events/device/undefinedModelLabel'
import { settings } from 'app/_shared/session'
import { get } from 'lib/sequence/component/state/value'
import setSerialFieldState from 'app/_shared/events/device/setSerialFieldState'
import clean from 'app/_shared/events/util/clean'
import noHistory from 'app/_shared/hook/action/noHistory'
import seqModelRead from 'lib/sequence/model/api/read'

/**
 * Refreshes `model` but leaving its `installedAt` attribute "intact".
 *
 * @param {Gaia.Model.Spec} model model to refresh
 * @param {Object} item           data to refresh the model with
 * @param {Object} options        refresh options
 * @return {Promise<void>}
 */
const refreshServiceItem = async (model, item, options) => {
  const { installedAt } = model[G.CHILDREN]

  const installedAtCache = installedAt[G.CACHE]
  const installedAtRef = installedAt[G.STATE][G.REF]
  const hasInstalledAt = installedAtCache || installedAtRef

  await refresh(model, item, options)

  if (hasInstalledAt) {
    installedAt[G.CACHE] = installedAtCache
    installedAt[G.STATE][G.REF] = installedAtRef

    await seqModelRead(installedAt)({ [G.CHILDREN]: [] })
  }
}

/**
 * Shows a dialog informing the user that a device already exists and will be open in a new tab.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {function} okHandler          a function to be executed if the user clicks 'ok'
 */
const showDeviceAlreadyExistsDialog = (module, okHandler) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  eventBus.dispatch(eventBus.type(G.DATA, G.UNDO), {
    title: module[G.ADAPTER][G.INTL]._t(
      'dialog.deviceAlreadyExists.title',
      {
        ns: 'device',
        _key: 'dialog.deviceAlreadyExists.title',
        defaultValue: 'Device already registered',
      },
    ),
    text: module[G.ADAPTER][G.INTL]._t(
      'dialog.deviceAlreadyExists.text',
      {
        ns: 'device',
        _key: 'dialog.deviceAlreadyExists.text',
        defaultValue: 'You have already registered a device with this serial number. Please check '
          + 'the entered numbers and try again or proceed to view the registered '
          + 'device.',
      },
    ),
    children: {
      cancel: {
        key: 'cancel',
        value: module[G.ADAPTER][G.INTL]._t(
          'button.cancel',
          {
            ns: 'common',
            _key: 'button.cancel',
            defaultValue: 'Cancel',
          },
        ),
      },
      ok: {
        key: 'ok',
        value: module[G.ADAPTER][G.INTL]._t(
          'button.goToDevice',
          {
            ns: 'device',
            _key: 'button.goToDevice',
            defaultValue: 'Go to device',
          },
        ),
      },
    },
    okHandler,
  })
}

/**
 * Attempts to obtain the product with key `event.detail.key` and sets it as the product
 * property of the current model.
 *
 * @param {Gaia.AppModule.Spec} module    the current module composition object
 * @param {Gaia.Component.Spec} component the current action's main component
 * @param {Event|Object} eventOrItem      the event object that triggered this handler
 * @param {Object} eventOrItem.detail     custom event details
 * @returns {Promise<void>} void
 */
// eslint-disable-next-line no-unused-vars
const setItem = async (module, component, eventOrItem) => {
  const model = module[G.MODEL]
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { serial: serialField, btnMore, existingHelp } = sequenceComponentFind(actionComponent)
  const { item: selectedItem = eventOrItem } = eventOrItem?.detail || {}
  const item = expose(selectedItem) || {}
  const { serviceItem, name, product } = item
  const serial = get(serialField)
  const synthetic = serviceItem?._rev && serviceItem._rev?.charAt(0) === '0'

  if (serviceItem?.key && !synthetic && !serviceItem?.value.notConfirmed) {
    // if a serviceItem exists, redirect the user to it
    showDeviceAlreadyExistsDialog(module, async () => {
      await noHistory(module)()
      await clean(module, null, null)
      await detail(module, component, new PlatformEvent('click', serviceItem))
    })
  } else {
    // saving found serviceItem
    serviceItem
      ? await refreshServiceItem(model, serviceItem, { shallow: true })
      && serviceItem?.key
      && serviceItem?.value.notConfirmed
      && setKey(true, G.BULK, model[G.STATE])
      // if we allow devices without product, the user can create the device from nothing
      : await refreshServiceItem(model, { value: {
        serial,
        name: name || await undefinedModelLabel(module),
      } }, { shallow: true })

    const nextTab = product || synthetic || settings.suppressProductDevices ? CONFIRM : LIST

    synthetic || (product || settings.suppressEquipmentDevices)
    && (!serviceItem?.key || serviceItem?.value.notConfirmed)
      ? await setStepTab(nextTab)(module, component, eventOrItem) // setting step's 'confirm' tab
      : show(existingHelp)
      && !settings.serialFieldConfig?.strict
      && show(btnMore) // if no product is found, we show the help buttons
  }

  await setSerialFieldState(module, serialField, [serial, item])
}

export default curry(setItem)
